import type { JSX } from 'preact';
import { forwardRef } from 'preact/compat';
import { useId, useState } from 'preact/hooks';
import { BACKDROP_TRANSITION, ICON_PRESETS, LAUNCHER_SHADOW, resolveIcon, resolveShape } from '@feedback/shared';
import type { WidgetConfig } from '../api/types';
import { strings } from '../strings';
import { DefaultIcon } from './default-icon';
import { darken, HOVER_DARKEN_AMOUNT } from './colors';
import { BRAND_ACCENT, BRAND_ACCENT_TEXT, BRAND_FONT_STACK, clampBorderRadius } from './brand';

export interface ButtonProps
  extends Omit<JSX.HTMLAttributes<HTMLButtonElement>, 'onClick' | 'style' | 'config'> {
  config: WidgetConfig;
  onOpen: () => void;
  /** Extra inline styles merged over the appearance styles below — used by `App` to position. */
  style?: JSX.CSSProperties;
  /**
   * The drawer is open. The tooltip would otherwise stay up over the footer
   * actions, because hover state does not clear when the button is hidden.
   */
  concealed?: boolean;
}

const DEFAULT_LABEL = 'Feedback';

/**
 * The real feedback trigger button, replacing Task 4's placeholder. Purely presentational
 * and drag-agnostic: `App` composes it with `useDraggable`, positioning it via the `style`
 * prop and spreading pointer handlers through `...rest`, and wraps `onOpen` so a drag's
 * trailing click doesn't reopen the widget (see `useDraggable`'s `consumeWasDragged()`).
 *
 * Hover is tracked with local state and applied via inline styles (mouse enter/leave), which
 * uses events disjoint from the pointer-based drag handlers spread through `...rest`, so hover
 * never interferes with dragging.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { config, onOpen, style, concealed = false, ...rest },
  ref,
) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [placement, setPlacement] = useState<TooltipPlacement>({ below: false, align: 'center' });
  const tooltipId = useId();

  /*
   * `!== false`, not `=== true`: `tooltipEnabled` is optional on `WidgetConfig`
   * because it crosses a network boundary and arrives from a config endpoint
   * that predates it. Treating "absent" as "off" would silently disable the
   * tooltip in exactly the cases nobody is looking at.
   */
  const tooltipText = config.tooltipEnabled === false
    ? null
    : config.tooltipText?.trim() || strings.tooltip.defaultText;
  const showTooltip = !concealed && tooltipText !== null && (hovered || focused);

  const label = config.label?.trim() ? config.label : DEFAULT_LABEL;
  const bgColor = config.bgColor ?? BRAND_ACCENT;
  const textColor = config.textColor ?? BRAND_ACCENT_TEXT;
  const iconColor = config.iconColor ?? BRAND_ACCENT_TEXT;

  // Hover derivation: an explicit hoverBgColor wins; otherwise auto-darken the base bg. The
  // text/icon color swaps to hoverTextColor only when configured, else keeps the base color.
  const hoverBg = config.hoverBgColor ?? darken(bgColor, HOVER_DARKEN_AMOUNT);
  const hoverText = config.hoverTextColor ?? textColor;
  const effectiveBg = hovered ? hoverBg : bgColor;
  const effectiveText = hovered ? hoverText : textColor;
  const effectiveIcon = hovered && config.hoverTextColor ? config.hoverTextColor : iconColor;

  // See brand.ts's clampBorderRadius: also guards non-number/NaN, not just null/undefined.
  const radius = clampBorderRadius(config.borderRadius);

  const shape = resolveShape(config.shape ?? 'RECTANGLE', {
    borderRadius: radius,
    maskUrl: config.shapeAssetUrl,
  });

  // An icon-only shape has a fixed aspect ratio, so a label would stretch it,
  // and a stretched CUSTOM mask distorts with it. This used to carry a second
  // reason — that the launcher changes width on hover when the drag grip
  // appears, deforming the mask as the pointer arrives — which no longer
  // happens: the grip is out of flow on every shape now (see `DragGrip`), so
  // hover changes no dimension. The label reason stands on its own. Forcing
  // the icon on also guarantees the button is never rendered empty, which a
  // bare `&& !iconOnly` on the label would allow for buttonMode: 'TEXT'.
  const showIcon = shape.iconOnly || config.buttonMode === 'ICON' || config.buttonMode === 'ICON_TEXT';
  const showLabel = !shape.iconOnly && (config.buttonMode === 'TEXT' || config.buttonMode === 'ICON_TEXT');

  const buttonStyle: JSX.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: (showIcon || showLabel) ? '8px' : undefined,
    border: 'none',
    // Not merely "no background declared" — explicitly transparent. Removing
    // the background when it moved to the shape backdrop did not make this
    // button paint nothing: a <button> defaults to the UA stylesheet's
    // `background-color: ButtonFace` (near-white), and that rectangle sits
    // BEHIND the backdrop across the full border box. The radius-based shapes
    // hide it because their backdrop covers that whole box, but every clipped
    // shape cuts part of the backdrop away and uncovers it — shipping white
    // wedges in PARALLELOGRAM's two clipped corners, NOTCH's chamfer, and
    // everywhere outside a CUSTOM mask's silhouette. The shorthand, not
    // `backgroundColor`, so a UA that also supplies a background IMAGE on
    // form controls is cleared too.
    background: 'transparent',
    // Paint-inert now that the button's background and shadow both live on
    // the backdrop (`overflow` stays `visible`, so this clips nothing) — kept
    // purely so the UA focus outline traces the shape instead of a square
    // corner around a rounded or teardrop launcher.
    borderRadius: `${radius}px`,
    // `relative` so the backdrop's `inset: 0` resolves against this button and
    // so the content below paints above it (a positioned z-index:0 element
    // otherwise paints over in-flow content).
    position: 'relative',
    // 12px of block padding on a labelled launcher. The 20px icon plus 12px
    // above and below is 44px, the minimum target. 10px left the button at 40.
    padding: showLabel ? `12px ${16 + shape.extraPaddingX}px` : '12px',
    cursor: 'pointer',
    // Was `inherit`, which resolved to the HOST PAGE's font — the launcher sits
    // in a shadow root attached to the customer's document, so it has never
    // rendered in a Tenger face on any deployment.
    fontFamily: config.fontFamily ?? BRAND_FONT_STACK,
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: 1,
    // Same host-inheritance problem `.ft-overlay` resets in styles.ts: this
    // button is styled entirely via inline styles (no class, no cascade of
    // its own), and today it only reads `text-transform: none` by accident —
    // the UA stylesheet happens to reset `button` elements. Inline styles
    // outrank an inherited value regardless, so setting these explicitly
    // gives the launcher the same protection deliberately instead of by luck.
    textTransform: 'none',
    letterSpacing: 'normal',
    color: effectiveText,
    touchAction: 'none',
    ...motionStyle(),
    ...shape.buttonStyle,
    ...style,
  };

  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      onClick={onOpen}
      onMouseEnter={(event) => {
        setHovered(true);
        // Flip below when there is no room above, and anchor to whichever edge
        // has room horizontally. Measured from the event's own target rather
        // than a ref, so this component stays ref-free for the caller — `App`
        // already owns the forwarded ref for its size measurement.
        setPlacement(placeTooltip(event.currentTarget));
      }}
      onMouseLeave={() => setHovered(false)}
      onFocus={(event) => {
        setFocused(true);
        // Keyboard users never fire `onMouseEnter`, so without this a Tab'd-to
        // button near an edge would keep `placement`'s initial value and render
        // its tooltip off-screen — failing exactly the users it exists to help.
        setPlacement(placeTooltip(event.currentTarget));
      }}
      onBlur={() => setFocused(false)}
      aria-describedby={showTooltip ? tooltipId : undefined}
      style={buttonStyle}
      {...rest}
    >
      <span
        data-ft-shape=""
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          backgroundColor: effectiveBg,
          // Never intercept the pointer: the button owns every interaction,
          // and the backdrop covers all of it.
          pointerEvents: 'none',
          ...backdropMotionStyle(),
          ...(shape.clipped
            ? { filter: LAUNCHER_SHADOW.drop }
            : { boxShadow: LAUNCHER_SHADOW.box }),
          ...shape.backdropStyle,
        }}
      />
      {hovered || focused ? (
        // Always out of flow, on every shape: the hovered launcher must occupy
        // exactly the box the resting one does. See `DragGrip`'s doc comment
        // for the reasoning this replaced.
        <DragGrip
          color={effectiveIcon}
          corner={shape.iconOnly}
          insetX={4 + shape.extraPaddingX}
        />
      ) : null}
      {showIcon ? renderLauncherIcon(config.iconUrl, effectiveIcon) : null}
      {showLabel ? <span style={{ position: 'relative' }}>{label}</span> : null}
      {showTooltip ? (
        <span id={tooltipId} role="tooltip" style={tooltipStyle(placement, radius)}>
          {tooltipText}
        </span>
      ) : null}
    </button>
  );
});

/**
 * The launcher is draggable and nothing says so — this is the affordance, not
 * a handle. The WHOLE button stays the drag surface: making these ~10px of dots
 * the only grab point would give touch users a target they cannot hit, and
 * would mean a user who never hovers can never move the button at all.
 *
 * Squares, not the dots of the usual grip glyph: the brand guide prohibits
 * circles, and at 3px the two read identically. The guard test only walks the
 * portal tree, so nothing would have caught this — it is a deliberate choice,
 * not an accident of scope.
 *
 * **The grip never occupies space in the button's box.** It is absolutely
 * positioned on every shape, so appearing on hover cannot change the measured
 * size of the launcher.
 *
 * This reverses the reasoning that used to live here, which is kept rather
 * than deleted because it was a considered position and the argument against
 * it is what makes the current shape make sense. It ran: rendering the grip
 * only on hover is why the button changes width, and that is correct in both
 * positions and needs no handling, because a button on its default anchor is
 * placed at `vw - width - 24` and `useDraggable` re-derives that from the
 * measured width so the right edge stays put, while a dragged button is
 * user-owned and simply grows rightward from a fixed left edge. Both halves
 * are true and neither is the point: the launcher sits on a CUSTOMER's page,
 * and a badge that changes size as the pointer arrives is the widget moving
 * their layout, whichever edge stays anchored. The icon-only shapes were only
 * ever the loudest case rather than the only one — `aspect-ratio: 1` read the
 * extra width back as extra height, so the growth was ~40% in both axes and
 * impossible to miss.
 *
 * `useDraggable`'s re-anchor from the measured width is still needed, but it
 * is now about config-driven size changes (label, shape, padding) rather than
 * about hover.
 *
 * Two placements, and the difference is which corner is painted rather than a
 * layout distinction:
 *
 * - `corner` (icon-only: `SQUARE`/`TEARDROP`/`CUSTOM`) pins the grip top-right,
 *   which is where it already shipped. Unchanged.
 * - Otherwise it centres vertically against the left edge, the side the grip
 *   has always been on for these shapes (it renders before the icon and label
 *   in flex order). `insetX` is `4 + extraPaddingX`, so it parks inside padding
 *   the button ALREADY has and adds none: `PARALLELOGRAM` clips its top-left
 *   corner, and `extraPaddingX` is the allowance that exists to keep the label
 *   clear of exactly that slant. Reusing it rather than inventing a second
 *   constant means the two can never disagree. `NOTCH` clips its BOTTOM-RIGHT,
 *   so a left-anchored grip is clear of it either way.
 */
function DragGrip({
  color,
  corner,
  insetX,
}: {
  color: string;
  corner: boolean;
  insetX: number;
}) {
  return (
    <svg
      data-ft-grip=""
      width="10"
      height="16"
      viewBox="0 0 10 16"
      aria-hidden="true"
      focusable="false"
      style={{
        display: 'block',
        // Always absolute, so the grip is out of flow and costs the button no
        // width. `zIndex: 1` paints it above the shape backdrop, the same way
        // `position: relative` does for the in-flow content — see the
        // backdrop's own comment in `Button`.
        position: 'absolute',
        zIndex: 1,
        ...(corner
          ? { top: '4px', right: '4px' }
          : { left: `${insetX}px`, top: '50%', transform: 'translateY(-50%)' }),
      }}
    >
      {[2, 7, 12].map((y) => (
        <g key={y}>
          <rect x="1" y={y} width="3" height="3" fill={color} />
          <rect x="6" y={y} width="3" height="3" fill={color} />
        </g>
      ))}
    </svg>
  );
}

/**
 * Renders the launcher icon from the resolved `iconUrl`:
 *  - preset  → an inline SVG whose inner markup is the shared preset constant, colored via
 *              `color` so the markup's `currentColor` strokes/fills pick up `iconColor`;
 *  - url     → an `<img>` pointing at the custom icon URL;
 *  - default → the bundled `DefaultIcon`.
 *
 * Exported for the edge tab, so both launchers draw an application's icon with one function
 * (I22656). The tab passes 16 for `size` and `currentColor` for the color, and keeps its own
 * conversation glyph when the application chose no icon.
 */
export function renderLauncherIcon(iconUrl: string | null | undefined, iconColor: string, size = 20) {
  const resolved = resolveIcon(iconUrl);

  if (resolved.kind === 'preset') {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
        // `relative` so this paints above the shape backdrop — see the backdrop's
        // own comment in `Button` for why an unpositioned sibling would not.
        // Outline presets take their ink from `stroke`. The question mark is a
        // filled drawing, and inheriting `stroke-width: 1` outlines it: the
        // ink comes out about 38% heavier and the edges go soft. `none` keeps
        // that one mark a fill.
        style={{
          display: 'block',
          color: iconColor,
          fill: iconColor,
          stroke: resolved.name === 'question' ? 'none' : iconColor,
          position: 'relative',
        }}
        // SAFE: ICON_PRESETS values are our own trusted, author-controlled constants
        // (see packages/shared/src/icons.ts) — never user input — so injecting them as
        // SVG innerHTML carries no XSS risk.
        dangerouslySetInnerHTML={{ __html: ICON_PRESETS[resolved.name] }}
      />
    );
  }

  if (resolved.kind === 'url') {
    return (
      <img
        src={resolved.url}
        alt=""
        width={size}
        height={size}
        // `relative` so this paints above the shape backdrop — see the
        // backdrop's own comment in `Button` for why an unpositioned sibling
        // would not.
        style={{ display: 'block', position: 'relative' }}
      />
    );
  }

  return <DefaultIcon color={iconColor} />;
}

/**
 * `prefers-reduced-motion`, read fresh on every render rather than cached: it's a media query,
 * not a static capability, and can flip while the widget is mounted (OS setting change, or a
 * test toggling it). Shared by `motionStyle` and `backdropMotionStyle` — the button and the
 * shape backdrop are separate elements with separate inline styles, so each has to declare its
 * own `transition: none`; there is no single ancestor rule that would cover both.
 */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    (() => {
      try {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      } catch {
        return false;
      }
    })()
  );
}

/**
 * Transition styling for the button itself. Honors `prefers-reduced-motion`: no
 * transition/animation when reduced, otherwise animates `transform` and `color` — the only two
 * hover-affected properties still painted on the button. `background-color` and `box-shadow`
 * moved to the shape backdrop along with the paint they used to animate; see
 * `backdropMotionStyle` below. (Split out rather than one shared transition list: the button and
 * the backdrop are different elements, so a property one of them doesn't set is simply never
 * animated on it — listing it anyway would be dead weight, not a harmless no-op.)
 */
function motionStyle(): JSX.CSSProperties {
  return prefersReducedMotion()
    ? { transition: 'none', animation: 'none' }
    : { transition: 'transform 0.15s ease, color 0.15s ease' };
}

/**
 * Transition styling for the shape backdrop. `background-color`, `filter` and `box-shadow` all
 * live here now (see the backdrop's own comment in `Button`), so the fade that used to happen on
 * the button has to happen here instead — without this, the launcher's background would SNAP on
 * hover while `color` (still on the button, still transitioning via `motionStyle`) kept fading,
 * breaking a RECTANGLE launcher's hover feel relative to before this task. The property list
 * itself is `BACKDROP_TRANSITION` from `@feedback/shared` — see that constant's own comment for
 * why it isn't just a local literal.
 */
function backdropMotionStyle(): JSX.CSSProperties {
  return prefersReducedMotion() ? { transition: 'none' } : { transition: BACKDROP_TRANSITION };
}

/** Space (px) the tooltip needs above the button before it flips below. */
const TOOLTIP_CLEARANCE_PX = 44;

/**
 * Widest the tooltip may grow before its text wraps. The copy is admin-authored
 * free text with no length limit, so it cannot be assumed short — an unbounded
 * `nowrap` tooltip on a launcher parked at the right edge simply ran off the
 * screen and was cropped mid-sentence.
 */
const TOOLTIP_MAX_WIDTH_PX = 240;

/** Which corner of the button the tooltip hangs from. */
export interface TooltipPlacement {
  below: boolean;
  align: 'start' | 'center' | 'end';
}

/**
 * Shared by the mouse-enter and focus handlers so both reveal paths (pointer
 * and keyboard) agree on where the tooltip renders — measured from the event's
 * own target rather than a ref, since `App` owns the forwarded one.
 *
 * Horizontal alignment matters because the launcher is draggable and its
 * default anchor is the bottom-RIGHT corner: a centred tooltip wider than the
 * button overhangs on both sides, and at the right edge that half is off-screen.
 * Anchoring to whichever edge has room keeps it visible without measuring the
 * tooltip itself (which does not exist yet at this point).
 */
function placeTooltip(target: HTMLElement): TooltipPlacement {
  const rect = target.getBoundingClientRect();
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : Infinity;
  const centre = rect.left + rect.width / 2;
  const halfWidth = TOOLTIP_MAX_WIDTH_PX / 2;

  return {
    below: rect.top < TOOLTIP_CLEARANCE_PX,
    align:
      centre + halfWidth > viewportWidth ? 'end' : centre - halfWidth < 0 ? 'start' : 'center',
  };
}

/**
 * The tooltip is an absolutely-positioned child of the button, not a sibling:
 * `App` positions the button with `position: fixed`, which makes it the
 * containing block, so the tooltip follows it around for free — no second
 * positioned element to keep in sync, and nothing extra for `useDraggable` to
 * move. Out of flow, so it adds nothing to the button's measured footprint.
 */
function tooltipStyle({ below, align }: TooltipPlacement, radius: number): JSX.CSSProperties {
  const horizontal: JSX.CSSProperties =
    align === 'center'
      ? { left: '50%', transform: 'translateX(-50%)' }
      : align === 'end'
        ? { right: '0' }
        : { left: '0' };

  return {
    position: 'absolute',
    ...horizontal,
    ...(below ? { top: 'calc(100% + 8px)' } : { bottom: 'calc(100% + 8px)' }),
    padding: '6px 10px',
    borderRadius: `${radius}px`,
    background: BRAND_ACCENT,
    color: BRAND_ACCENT_TEXT,
    fontFamily: BRAND_FONT_STACK,
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: 1.4,
    // `max-content` keeps short copy compact and hugging the text; `max-width`
    // is what stops long copy from running off the screen, and `normal` lets it
    // wrap onto a second line instead. Was `nowrap`, which could do neither.
    width: 'max-content',
    maxWidth: `${TOOLTIP_MAX_WIDTH_PX}px`,
    whiteSpace: 'normal',
    // Explicit, not inherited: wrapped copy needs a predictable alignment, and
    // the launcher is styled entirely inline inside a host page whose own
    // `text-align` would otherwise flow into it (see the button's own reset).
    textAlign: 'left',
    // Never intercept the pointer — a tooltip that can be hovered can trap the
    // hover state, and this one sits over the page the user is reporting on.
    pointerEvents: 'none',
  };
}
